/**
 * WatchdogCore - Main orchestrator
 * 
 * Coordinates all collectors, middleware, and transport
 * Implements the complete event pipeline
 */

import { EventBus } from './EventBus.js';
import { MiddlewareManager } from './MiddlewareManager.js';
import { ConfigManager } from './ConfigManager.js';
import type { WatchdogConfig } from '../types.js';
import { generateSessionId, BreadcrumbManager } from '../utils/session.js';
import { generateBaseMetadata } from '../models/BaseEvent.js';
import { TransportManager } from '../strategies/transport/index.js';
import { RRWebPrivacyConfig } from '../strategies/privacy/RRWebPrivacyConfig.js';
import type { CollectedEvent, EventBatch } from '../middleware/types.js';

// Collectors
import { WebVitalsCollector } from '../collectors/WebVitalsCollector.js';
import { ErrorCollector } from '../collectors/ErrorCollector.js';
import { PerformanceCollector } from '../collectors/PerformanceCollector.js';
import { BehaviorCollector } from '../collectors/BehaviorCollector.js';
import { NetworkCollector } from '../collectors/NetworkCollector.js';
import { SessionRecorder } from '../collectors/SessionRecorder.js';
import { BaseCollector } from '../collectors/BaseCollector.js';

// Built-in middleware
import {
  BreadcrumbEnricher,
  SessionEnricher,
  ViewportEnricher,
  ConnectionEnricher,
  type SessionContext
} from '../middleware/built-in/index.js';

export class WatchdogCore {
  private config: ConfigManager;
  private eventBus: EventBus;
  private middlewareManager: MiddlewareManager;
  private transportManager: TransportManager;
  private breadcrumbManager: BreadcrumbManager;
  
  private sessionId: string;
  private userId: string | null;
  
  private collectors: BaseCollector[] = [];
  private sessionRecorder: SessionRecorder | null = null;
  
  // Event buffering
  private eventBuffer: CollectedEvent[] = [];
  private flushTimer: number | null = null;
  
  // Session context for middleware
  private sessionContext: SessionContext = {
    sessionStartTime: Date.now(),
    pageViews: 1,
    totalClicks: 0,
    totalKeystrokes: 0,
    maxScrollDepth: 0
  };

  // Unload handlers
  private visibilityHandler: (() => void) | null = null;
  private beforeUnloadHandler: (() => void) | null = null;
  private pagehideHandler: (() => void) | null = null;

  constructor(userConfig: WatchdogConfig) {
    this.config = new ConfigManager(userConfig);
    this.sessionId = generateSessionId();
    this.userId = this.config.get('userId') || null;
    
    // Initialize core components
    this.eventBus = new EventBus(this.config.get('debug'));
    this.middlewareManager = new MiddlewareManager({
      sessionId: this.sessionId,
      userId: this.userId,
      debug: this.config.get('debug')
    });
    this.transportManager = new TransportManager([], this.config.get('debug'));
    this.breadcrumbManager = new BreadcrumbManager(50);
    
    this.setupEventPipeline();
    this.setupBuiltInMiddleware();
    this.initializeCollectors();
    this.setupPeriodicFlush();
    this.setupUnloadHandlers();
    
    // Add initial breadcrumb
    this.breadcrumbManager.add('navigation', `Session started at ${window.location.href}`);
    
    this.log('Watchdog initialized successfully');
  }

  /**
   * Setup the event pipeline: EventBus -> Middleware -> Buffer -> Transport
   */
  private setupEventPipeline(): void {
    // Subscribe to collected events
    this.eventBus.subscribe('event:collected', (event: CollectedEvent) => {
      try {
        // Run through post-collect middleware
        const enrichedEvent = this.middlewareManager.executePostCollect(event);
        
        // Add to buffer
        this.eventBuffer.push(enrichedEvent);
        
        // Check if we should auto-flush
        if (this.eventBuffer.length >= this.config.get('batchSize')) {
          this.flush();
        }
      } catch (error) {
        this.error('Error in event pipeline:', error);
      }
    });

    // Subscribe to critical errors (send immediately)
    this.eventBus.subscribe('error:critical', async (errorEvent: CollectedEvent) => {
      if (!this.config.get('sendCriticalErrorsImmediately')) {
        return;
      }

      try {
        this.log('Sending critical error immediately');
        
        // Optional delay for batching
        const delay = this.config.get('criticalErrorDelay');
        if (delay > 0) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        await this.transportManager.send({
          endpoint: this.config.get('endpoint'),
          headers: {
            'Content-Type': 'application/json'
          },
          data: {
            apiKey: this.config.get('apiKey'),
            projectId: this.config.get('projectId'),
            sessionId: this.sessionId,
            events: [errorEvent],
            timestamp: Date.now(),
            priority: 'high'
          }
        });

        // Remove from buffer if it's there
        const index = this.eventBuffer.indexOf(errorEvent);
        if (index > -1) {
          this.eventBuffer.splice(index, 1);
        }
      } catch (error) {
        this.error('Failed to send critical error immediately:', error);
      }
    });
  }

  /**
   * Setup built-in middleware
   */
  private setupBuiltInMiddleware(): void {
    this.middlewareManager.register(new BreadcrumbEnricher(this.breadcrumbManager));
    this.middlewareManager.register(new SessionEnricher(this.sessionContext));
    this.middlewareManager.register(new ViewportEnricher());
    this.middlewareManager.register(new ConnectionEnricher());
  }

  /**
   * Initialize all collectors based on configuration
   */
  private initializeCollectors(): void {
    const debug = this.config.get('debug');

    // Web Vitals
    if (this.config.isEnabled('enableWebVitals')) {
      const collector = new WebVitalsCollector(
        this.eventBus,
        this.sessionId,
        this.userId,
        debug
      );
      collector.initialize();
      this.collectors.push(collector);
    }

    // Error Tracking
    if (this.config.isEnabled('enableErrorTracking')) {
      const collector = new ErrorCollector(
        this.eventBus,
        this.sessionId,
        this.userId,
        this.breadcrumbManager,
        {
          sendCriticalErrorsImmediately: this.config.get('sendCriticalErrorsImmediately'),
          debug
        }
      );
      collector.initialize();
      this.collectors.push(collector);
    }

    // Performance Tracking
    if (this.config.isEnabled('enablePerformanceTracking')) {
      const collector = new PerformanceCollector(
        this.eventBus,
        this.sessionId,
        this.userId,
        debug
      );
      collector.initialize();
      this.collectors.push(collector);
    }

    // Behavior Tracking
    if (this.config.isEnabled('enableBehaviorTracking')) {
      const collector = new BehaviorCollector(
        this.eventBus,
        this.sessionId,
        this.userId,
        this.breadcrumbManager,
        debug
      );
      collector.initialize();
      this.collectors.push(collector);
    }

    // Network Tracking (always collect for error tracking)
    const networkCollector = new NetworkCollector(
      this.eventBus,
      this.sessionId,
      this.userId,
      this.breadcrumbManager,
      {
        endpoint: this.config.get('endpoint'),
        sessionRecordingEndpoint: this.config.get('sessionRecordingEndpoint'),
        debug
      }
    );
    networkCollector.initialize();
    this.collectors.push(networkCollector);

    // Session Recording
    if (this.config.isEnabled('enableSessionRecording')) {
      const privacyConfig = new RRWebPrivacyConfig({
        maskAllInputs: this.config.get('maskAllInputs'),
        sensitiveSelectors: this.config.get('sensitiveSelectors'),
        enablePrivacyMode: this.config.get('enablePrivacyMode')
      });

      this.sessionRecorder = new SessionRecorder(
        this.eventBus,
        this.sessionId,
        this.userId,
        privacyConfig,
        {
          maxRecordingTime: this.config.get('maxRecordingTime'),
          segmentDuration: this.config.get('segmentDuration'),
          maxSegmentSize: this.config.get('maxSegmentSize'),
          debug
        }
      );
      this.sessionRecorder.initialize();
    }
  }

  /**
   * Setup periodic flush
   */
  private setupPeriodicFlush(): void {
    this.flushTimer = window.setInterval(() => {
      this.flush();
    }, this.config.get('flushInterval'));
  }

  /**
   * Setup unload handlers to flush data before page unload
   */
  private setupUnloadHandlers(): void {
    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.flushSync();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.beforeUnloadHandler = () => {
      this.flushSync();
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    this.pagehideHandler = () => {
      this.flushSync();
    };
    window.addEventListener('pagehide', this.pagehideHandler);
  }

  /**
   * Flush events asynchronously
   */
  async flush(): Promise<void> {
    if (this.eventBuffer.length === 0) {
      return;
    }

    const eventsToSend = [...this.eventBuffer];
    this.eventBuffer = [];

    // Run through pre-send middleware
    const batch: EventBatch = {
      sessionId: this.sessionId,
      events: eventsToSend,
      timestamp: Date.now()
    };

    const processedBatch = this.middlewareManager.executePreSend(batch);

    // Send asynchronously
    if ('requestIdleCallback' in window) {
      requestIdleCallback(async () => {
        await this.sendBatch(processedBatch);
      });
    } else {
      setTimeout(async () => {
        await this.sendBatch(processedBatch);
      }, 0);
    }
  }

  /**
   * Flush events synchronously (for page unload)
   */
  flushSync(): void {
    if (this.eventBuffer.length === 0) {
      return;
    }

    const batch: EventBatch = {
      sessionId: this.sessionId,
      events: this.eventBuffer,
      timestamp: Date.now()
    };

    const processedBatch = this.middlewareManager.executePreSend(batch);

    // Send synchronously using beacon or XHR
    // Include auth in body for sendBeacon compatibility
    const payloadWithAuth = {
      ...processedBatch,
      apiKey: this.config.get('apiKey'),
      projectId: this.config.get('projectId')
    };
    
    this.transportManager.sendSync({
      endpoint: this.config.get('endpoint'),
      headers: {
        'Content-Type': 'application/json'
      },
      data: payloadWithAuth
    });

    this.eventBuffer = [];
  }

  /**
   * Send batch to backend
   */
  private async sendBatch(batch: EventBatch): Promise<void> {
    try {
      // Include auth in body for sendBeacon compatibility
      const payloadWithAuth = {
        ...batch,
        apiKey: this.config.get('apiKey'),
        projectId: this.config.get('projectId')
      };
      
      await this.transportManager.send({
        endpoint: this.config.get('endpoint'),
        headers: {
          'Content-Type': 'application/json'
        },
        data: payloadWithAuth
      });

      this.log(`Successfully sent ${batch.events.length} events`);
    } catch (error) {
      this.error('Failed to send batch:', error);
      // Re-queue events on failure
      this.eventBuffer.unshift(...batch.events);
    }
  }

  /**
   * Register a custom middleware
   */
  registerMiddleware(middleware: any): void {
    this.middlewareManager.register(middleware);
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(featureName: string, context: Record<string, any> = {}): void {
    if (!this.config.isEnabled('enableBusinessMetrics')) return;

    const baseMetadata = generateBaseMetadata(this.sessionId, this.userId);

    const event: CollectedEvent = {
      ...baseMetadata,
      event_category: 'behavior',
      event_type: 'feature_usage',
      feature_name: featureName,
      context_json: JSON.stringify(context)
    };

    this.eventBus.emitSync('event:collected', event);
    this.breadcrumbManager.add('custom', `Feature used: ${featureName}`, context);

    if (this.sessionRecorder) {
      this.sessionRecorder.addCustomEvent('feature-usage', { feature: featureName, context });
    }
  }

  /**
   * Track conversion
   */
  trackConversion(event: string, value?: number, metadata?: Record<string, any>): void {
    if (!this.config.isEnabled('enableBusinessMetrics')) return;

    const baseMetadata = generateBaseMetadata(this.sessionId, this.userId);

    const conversionEvent: CollectedEvent = {
      ...baseMetadata,
      event_category: 'behavior',
      event_type: 'conversion',
      conversion_event: event,
      conversion_value: value || null,
      context_json: metadata ? JSON.stringify(metadata) : null
    };

    this.eventBus.emitSync('event:collected', conversionEvent);
    this.breadcrumbManager.add('custom', `Conversion: ${event}`, { value, metadata });

    if (this.sessionRecorder) {
      this.sessionRecorder.addCustomEvent('conversion', { event, value, metadata });
    }
  }

  /**
   * Add custom breadcrumb
   */
  addCustomBreadcrumb(message: string, data?: Record<string, any>): void {
    this.breadcrumbManager.add('custom', message, data);

    if (this.sessionRecorder) {
      this.sessionRecorder.addCustomEvent('breadcrumb', { message, data });
    }
  }

  /**
   * Update user ID
   */
  updateUserId(newUserId: string): void {
    this.userId = newUserId;
    this.config.update('userId', newUserId);
    this.middlewareManager.updateContext({ userId: newUserId });
    
    // Update all collectors
    this.collectors.forEach(collector => {
      collector.updateContext(undefined, newUserId);
    });

    if (this.sessionRecorder) {
      this.sessionRecorder.updateContext(undefined, newUserId);
    }

    this.log('User ID updated', { newUserId });
  }

  /**
   * Get current status
   */
  getStatus(): {
    isRecording: boolean;
    sessionId: string;
    bufferSize: number;
    collectors: string[];
  } {
    return {
      isRecording: this.sessionRecorder?.getIsInitialized() || false,
      sessionId: this.sessionId,
      bufferSize: this.eventBuffer.length,
      collectors: this.collectors.map(c => c.getName())
    };
  }

  /**
   * Pause session recording
   */
  pauseRecording(): void {
    if (this.sessionRecorder) {
      this.sessionRecorder.pause();
    }
  }

  /**
   * Resume session recording
   */
  resumeRecording(): void {
    if (this.sessionRecorder) {
      this.sessionRecorder.resume();
    }
  }

  /**
   * Destroy and cleanup
   */
  destroy(): void {
    this.log('Destroying Watchdog');

    // Clear flush timer
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Flush remaining events
    this.flushSync();

    // Destroy all collectors
    this.collectors.forEach(collector => collector.destroy());
    this.collectors = [];

    if (this.sessionRecorder) {
      this.sessionRecorder.destroy();
      this.sessionRecorder = null;
    }

    // Remove unload handlers
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
    if (this.pagehideHandler) {
      window.removeEventListener('pagehide', this.pagehideHandler);
    }

    // Clear event bus
    this.eventBus.clear();

    this.log('Watchdog destroyed');
  }

  private log(message: string, data?: any): void {
    if (this.config.get('debug')) {
      console.log(`[Watchdog] ${message}`, data || '');
    }
  }

  private error(message: string, error?: any): void {
    console.error(`[Watchdog] ${message}`, error || '');
  }
}

